import { Component, type OnInit, inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { type Observable } from 'rxjs';

import { ReferenceValueService } from '../../../modules/master-data/reference-values/reference-value.service';
import { type Language } from '../../../modules/user-management/users/user.model';
import { allowedDocumentTypes, allowedDocumentExtensions, fileSizeLimit } from '../../data/data-types.data';
import { CustomToastrService } from '../../services/custom-toastr.service';
import { LanguageService } from '../../services/language.service';
export interface ConfirmDialogResponse {
  comment: string | null;
  selectedFiles: File[] | null;
  selectedLanguageId: string | null;
  messageTemplate: string | null;
}
@Component({
  selector: 'app-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.scss',
  standalone: false
})
export class ConfirmDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ConfirmDialogComponent>>(MatDialogRef);
  data = inject<{
    title: string;
    note: string;
    message: string;
    message2: string;
    message3: string;
    acceptLabel: string;
    cancelLabel: string;
    name?: string;
    requireComment?: boolean;
    uploadingFiles?: boolean;
    stagedDocuments?: File[];
    languageDropdown?: boolean;
    messageTemplate?: string;
  }>(MAT_DIALOG_DATA);
  private translate = inject(TranslateService);
  languageService = inject(LanguageService);
  private customToastr = inject(CustomToastrService);
  private referenceValueService = inject(ReferenceValueService);

  comment = new FormControl('', Validators.required);
  localStagedFiles: File[] = [];

  languages$!: Observable<Language[]>;
  selectedLanguageId: string | null = null;
  messageTemplate: string | null = null;
  eventDocumentUploadLimitInternalCode = 'eventDocumentUploadLimit';
  eventDocumentUploadLimit = 3;

  allowedDocumentTypes = allowedDocumentTypes;
  allowedDocumentExtensions = allowedDocumentExtensions;

  ngOnInit(): void {
    if (this.data.languageDropdown) {
      this.languages$ = this.languageService.getLanguagesForDropdown();
    }
    if (this.data.messageTemplate) {
      this.messageTemplate = this.data.messageTemplate; // initialize from dialog data
    }
    if (this.data.uploadingFiles) {
      this.referenceValueService.getFileUploadLimitByRefValInternalCode(this.eventDocumentUploadLimitInternalCode).subscribe({
        next: (refValue: any) => {
          this.eventDocumentUploadLimit = refValue ? parseInt(refValue) : 0;
        }
      });
    }
  }

  onConfirm(): void {
    const trimmedComment = this.comment.value?.trim();
    if (this.data.requireComment && !trimmedComment) {
      return;
    }
    this.dialogRef.close({
      comment: trimmedComment ?? null,
      selectedFiles: this.localStagedFiles,
      selectedLanguageId: this.selectedLanguageId,
      messageTemplate: this.messageTemplate?.trim() ?? null
    });
  }

  onDocumentSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files) {
      const newFiles = Array.from(input.files).filter(file => this.allowedDocumentTypes.includes(file.type));

      if (newFiles.length !== input.files.length) {
        this.customToastr.warning(this.translate.instant('COMMON.FILES_NOT_ADDED'), this.translate.instant('COMMON.WARNING'));
      }
      this.stageDocuments(newFiles);
    }
  }

  onDocumentDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files) {
      const newFiles = Array.from(event.dataTransfer.files).filter(file => this.allowedDocumentTypes.includes(file.type));

      if (newFiles.length !== event.dataTransfer.files.length) {
        console.error(this.translate.instant('COMMON.FILES_NOT_ADDED'));
      }
      this.stageDocuments(newFiles);
    } else {
      console.error(this.translate.instant('COMMON.FILES_NOT_ADDED'));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  removeLocalFile(index: number): void {
    if (index >= 0 && index < this.localStagedFiles.length) {
      this.localStagedFiles.splice(index, 1);
      this.customToastr.success(this.translate.instant('COMMON.FILE_DELETED_SUCCESSFULLY'));
    }
  }

  stageDocuments(newFiles: File[]): void {
    const oversizedFiles = newFiles.filter(file => file.size > fileSizeLimit);
    if (oversizedFiles.length > 0) {
      this.customToastr.warning(this.translate.instant('COMMON.MAX_FILE_SIZE_IS_5_MB'));
      return;
    }

    if (this.localStagedFiles.length + newFiles.length > this.eventDocumentUploadLimit) {
      this.customToastr.warning(this.translate.instant('COMMON.MAX_OF_3_FILES_CAN_BE_UPLOADED'));
      return;
    }

    if (newFiles.length > 0) {
      this.localStagedFiles.push(...newFiles); // ← display in dialog
    }
  }
}
